import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Search, Smartphone, Gauge, Globe,
  Palette, ShieldCheck, BarChart3, MapPin, Code2, Layers,
} from "lucide-react";
import { Eyebrow, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { useI18n } from "@/context/I18nContext";
import { WebdesignExplainers } from "@/components/explainers/WebdesignExplainers";

const CITIES = [
  { name: "Fulda", region: "Osthessen", lat: 50.5557, lng: 9.6808, distance: "30 min" },
  { name: "Bad Hersfeld", region: "Hessen", lat: 50.8688, lng: 9.7081, distance: "15 min" },
  { name: "Hünfeld", region: "Hessen", lat: 50.6797, lng: 9.7659, distance: "15 min" },
  { name: "Kassel", region: "Nordhessen", lat: 51.3127, lng: 9.4797, distance: "60 min" },
  { name: "Erfurt", region: "Thüringen", lat: 50.9787, lng: 11.0328, distance: "60 min" },
  { name: "Würzburg", region: "Bayern", lat: 49.7913, lng: 9.9534, distance: "90 min" },
];

const SITE_URL = "https://it-servicepoint.eu";

export const Webdesign = () => {
  const { t, lang } = useI18n();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_URL}/webdesign#service`,
      name: "Webdesign & SEO",
      alternateName: ["Webentwicklung", "Website erstellen", "Webseiten Programmierung"],
      provider: { "@id": `${SITE_URL}/#organization` },
      serviceType: "Web design and search engine optimisation",
      description:
        "Professionelle Webseiten-Entwicklung und SEO für KMUs – maßgeschneidert für jede Branche, mit Fokus auf Performance, Mobilfreundlichkeit und lokaler Sichtbarkeit in Osthessen, Nordhessen, Thüringen und Unterfranken.",
      areaServed: CITIES.map((c) => ({
        "@type": "City",
        name: c.name,
        containedInPlace: { "@type": "AdministrativeArea", name: c.region },
        geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng },
      })),
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Webdesign-Pakete",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Visitenkarten-Website" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "KMU-Webseite mit CMS" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Web-Anwendung / Custom Development" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Lokales SEO" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Webseiten-Hosting & Wartung" } },
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Webdesign & SEO", item: `${SITE_URL}/webdesign` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Was kostet eine professionelle Webseite in Fulda?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Eine professionelle Visitenkarten-Webseite startet bei rund 1.200 €, eine KMU-Webseite mit CMS und Mehrsprachigkeit zwischen 3.500 € und 8.000 €. Größere Web-Anwendungen und Online-Shops werden individuell kalkuliert. Wir machen ein verbindliches Festpreis-Angebot nach einem 30-minütigen Erstgespräch.",
          },
        },
        {
          "@type": "Question",
          name: "Wie lange dauert die Erstellung einer Webseite?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Eine kompakte Visitenkarten-Seite ist in 2–3 Wochen live. KMU-Webseiten mit CMS, Mehrsprachigkeit und SEO-Setup brauchen typischerweise 4–8 Wochen. Wir arbeiten in transparenten Sprints mit wöchentlichen Updates.",
          },
        },
        {
          "@type": "Question",
          name: "Übernehmen Sie auch das lokale SEO für Unternehmen in Fulda und Umgebung?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Ja. Lokales SEO ist Standard in jedem unserer Projekte: Google Business Profil, lokale Keyword-Optimierung, strukturierte Daten (Schema.org LocalBusiness), Verzeichnis-Einträge und Reputation Management für Fulda, Bad Hersfeld, Hünfeld, Kassel, Erfurt und Würzburg.",
          },
        },
        {
          "@type": "Question",
          name: "Kann ich meine Webseite nachträglich selbst bearbeiten?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Ja. Wir liefern jede Webseite mit einem einfach bedienbaren Content-Management-System (CMS), in dem Sie Texte, Bilder, Blog-Beiträge und neue Seiten selbst verwalten können. Eine Schulung gehört zum Lieferumfang.",
          },
        },
      ],
    },
  ];

  return (
    <div data-testid="webdesign-page" className="relative">
      <SEO
        title={lang === "de"
          ? "Webdesign & SEO Fulda · Webseiten für KMU in Hessen & Thüringen"
          : "Web Design & SEO Fulda · Websites for SMBs in Hessen & Thuringia"}
        description={lang === "de"
          ? "Professionelles Webdesign aus Fulda für Bad Hersfeld, Hünfeld, Kassel, Erfurt und Würzburg. Schnelle, mobile, suchmaschinen­optimierte Webseiten für jede Branche – inkl. CMS, Hosting und lokalem SEO."
          : "Professional web design from Fulda for Bad Hersfeld, Hünfeld, Kassel, Erfurt and Würzburg. Fast, mobile, SEO-friendly websites for every industry – incl. CMS, hosting and local SEO."}
        keywords="Webdesign Fulda, Webdesign Bad Hersfeld, Webdesign Hünfeld, Webdesign Kassel, Webdesign Erfurt, Webdesign Würzburg, Webseite erstellen lassen Fulda, SEO Agentur Osthessen, Webentwicklung Hessen, professionelle Webseite Mittelstand, lokales SEO, Webdesigner Fulda, KMU Webseite, Webseite Handwerker, Webseite Arztpraxis, Webseite Anwaltskanzlei"
        jsonLd={jsonLd}
        type="service"
      />

      {/* HERO with local cities */}
      <section className="relative overflow-hidden bg-soft border-b border-line">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>Webdesign · SEO · Hosting</Eyebrow>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-ink">
              Professionelle Webseiten,<br/>die in <span className="text-primary">Fulda, Kassel & Würzburg</span> gefunden werden.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-muted leading-relaxed">
              Wir bauen Webseiten für <strong className="text-ink">jede Branche</strong> – Handwerk, Arztpraxis, Kanzlei, Maschinenbau, Hotellerie oder Online-Shop. Schnell, mobil-optimiert, mit eingebautem lokalen SEO für Osthessen, Nordhessen, Thüringen und Unterfranken.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                data-testid="webdesign-hero-cta"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-medium hover:bg-primary-hover transition-colors shadow-soft"
              >
                Kostenfreies Erstgespräch
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+4966299153890"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-line bg-white text-ink hover:border-primary hover:text-primary transition-colors font-medium"
              >
                +49 (0) 6629 9153890
              </a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-line bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-primary mb-4">
                <MapPin size={14} /> Wir bedienen
              </div>
              <ul className="space-y-2.5">
                {CITIES.map((c) => (
                  <li key={c.name} className="flex items-center justify-between py-1.5 border-b border-line last:border-0">
                    <span className="font-display font-bold text-ink">{c.name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">{c.distance}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WEBSITE CHECK TEASER */}
      <section className="bg-ink text-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-14 sm:py-16 flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
          <div className="flex items-start md:items-center gap-5">
            <div className="w-14 h-14 shrink-0 bg-accent/15 border border-accent/40 flex items-center justify-center text-accent">
              <Gauge size={26} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Kostenlos · 60 Sekunden</div>
              <h3 className="mt-2 font-display font-black text-2xl sm:text-3xl tracking-tightest">Bevor Sie investieren: Kennen Sie den Score Ihrer aktuellen Webseite?</h3>
              <p className="mt-2 text-white/70 max-w-2xl">Score, Detail-Report und Handlungsempfehlungen per E-Mail – ohne Anmeldung.</p>
            </div>
          </div>
          <Link
            to="/webseiten-check"
            data-testid="webdesign-check-cta"
            className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors shrink-0"
          >
            Jetzt prüfen
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Was Sie bekommen"
            title="Mehr als nur eine schöne Webseite."
            sub="Eine moderne Webseite muss vier Dinge gleichzeitig leisten: gut aussehen, schnell laden, mobil funktionieren und in Google gefunden werden. Wir liefern alle vier – als Standard."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Palette, title: "Maßgeschneidertes Design", desc: "Kein Baukasten, keine Schablone. Jede Webseite wird auf Ihre Marke und Branche zugeschnitten." },
              { icon: Smartphone, title: "Mobil zuerst", desc: "Über 70 % Ihrer Besucher kommen vom Smartphone. Wir designen mobil zuerst und dann nach oben." },
              { icon: Gauge, title: "Höchste Performance", desc: "Ladezeiten unter 2 Sekunden. Google Core Web Vitals im grünen Bereich. Das spart Geld bei Google Ads." },
              { icon: Search, title: "SEO ab Tag 1", desc: "Saubere Seitenstruktur, semantisches HTML, Schema.org-Daten, Sitemap und robots.txt. Out of the box." },
              { icon: MapPin, title: "Lokales SEO", desc: "Optimiert für Service + Stadt-Suchen — damit Sie in Fulda, Bad Hersfeld, Kassel, Würzburg gefunden werden, nicht in Berlin." },
              { icon: Globe, title: "Mehrsprachigkeit", desc: "Deutsch und Englisch (oder weitere Sprachen) mit korrekten hreflang-Tags – wichtig für Export-orientierte Unternehmen." },
              { icon: Layers, title: "Content Management System", desc: "Sie pflegen Texte, Bilder, Blog und Angebote selbst. Eine Schulung ist inklusive." },
              { icon: ShieldCheck, title: "Sicher gehostet in Deutschland", desc: "Hosting auf unseren Servern in deutschen Rechenzentren. DSGVO-konform, SSL inklusive, tägliche Backups." },
              { icon: BarChart3, title: "Messung & Reporting", desc: "Datenschutz-konforme Analytics (z.B. Plausible oder Matomo), monatliches SEO-Reporting auf Wunsch." },
            ].map((f, i) => (
              <div key={f.title} className="border border-line bg-white p-8 lift" data-testid={`webdesign-feature-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <f.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-xl text-ink">{f.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WebdesignExplainers />

      {/* INDUSTRIES */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Branchen"
            title="Webseiten für jede Branche."
            sub="Vom Handwerksbetrieb in Hünfeld bis zur Anwaltskanzlei in Würzburg – wir haben Erfahrung in nahezu jeder Branche. Hier eine Auswahl."
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-line">
            {[
              "Handwerksbetriebe",
              "Arztpraxen & MVZ",
              "Anwalts- & Steuerkanzleien",
              "Maschinen- & Anlagenbau",
              "Online-Shops & E-Commerce",
              "Hotellerie & Gastronomie",
              "Vereine & Verbände",
              "Bildungseinrichtungen",
              "Architekten & Ingenieure",
              "Logistik & Spedition",
              "Immobilienmakler",
              "Coaches & Berater",
            ].map((b, i) => (
              <div key={b} className="bg-white p-6 hover:bg-soft transition-colors" data-testid={`webdesign-industry-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">{String(i + 1).padStart(2, "0")}</span>
                <div className="mt-3 font-display font-bold text-ink">{b}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-muted text-sm">
            Ihre Branche ist nicht dabei? <Link to="/kontakt" className="text-primary underline hover:text-primary-hover">Sprechen Sie uns an</Link> – wir haben mit Sicherheit ein vergleichbares Projekt schon umgesetzt.
          </p>
        </div>
      </section>

      {/* LOCAL CITIES SEO BLOCK */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Webdesign-Agentur in Ihrer Nähe</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink leading-[1.05] max-w-3xl">
            Lokal verwurzelt, regional aktiv.
          </h2>
          <p className="mt-6 max-w-2xl text-muted text-lg leading-relaxed">
            Unser Hauptstandort liegt in Schenklengsfeld bei Bad Hersfeld – mitten zwischen Fulda, Kassel, Erfurt und Würzburg. Diese kurze Distanz ist Ihr Vorteil: kurze Wege bei Vor-Ort-Terminen, schnelle Reaktionszeiten und ein echter Ansprechpartner statt anonymen Call-Center.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { city: "Fulda", text: "Vom Innenstadt-Einzelhandel bis zum produzierenden Mittelstand – Fulda ist unser wichtigster Markt für moderne KMU-Webseiten. Wir kennen die Region und das hiesige Publikum." },
              { city: "Bad Hersfeld", text: "Direkt vor unserer Haustür. Persönliche Termine, schnelle Vor-Ort-Hilfe und Webseiten, die zur Festspielstadt passen – modern, professionell, weltoffen." },
              { city: "Hünfeld", text: "Klein-, Mittel- und Kleinstunternehmen rund um die Konrad-Zuse-Stadt profitieren von unserer Nähe – Erstgespräche oft direkt vor Ort." },
              { city: "Kassel", text: "Nordhessens Wirtschaftszentrum mit starkem Mittelstand, vielen Praxen und Kanzleien. Wir bauen Webseiten, die in Kassel und Umgebung ranken." },
              { city: "Erfurt", text: "Thüringens Landeshauptstadt mit lebendiger Kultur- und Wirtschaftsszene. Webseiten mit lokalem SEO für ein Publikum, das per Bahn 90 Minuten entfernt ist." },
              { city: "Würzburg", text: "Im Norden Unterfrankens betreuen wir Unternehmen, Anwaltskanzleien und Hotels mit professioneller Online-Präsenz und lokaler Sichtbarkeit." },
            ].map((c) => (
              <article key={c.city} className="border border-line bg-white p-7 lift" data-testid={`webdesign-city-${c.city.toLowerCase().replace(" ", "-")}`}>
                <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-[0.25em]">
                  <MapPin size={14} /> {c.city}
                </div>
                <h3 className="mt-4 font-display font-bold text-lg text-ink">Webdesign in {c.city}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading eyebrow="Vorgehen" title="In 5 Schritten zur fertigen Webseite." />
          <ol className="mt-14 space-y-2">
            {[
              { title: "Kostenfreies Erstgespräch", body: "30 Minuten Video- oder Telefon-Call. Wir verstehen Ihre Branche, Zielgruppe und Ziele. Sie bekommen eine erste Einschätzung – ohne Verkaufs-Folien." },
              { title: "Festpreis-Angebot", body: "Innerhalb von 3 Werktagen erhalten Sie ein detailliertes Angebot mit Festpreis, Leistungsumfang, Meilensteinen und Zeitplan. Keine versteckten Kosten." },
              { title: "Design & Wireframes", body: "Wir entwerfen 2–3 Design-Varianten basierend auf Ihrem CI. Sie wählen aus, wir verfeinern. Klares Feedback statt endloser Korrekturschleifen." },
              { title: "Entwicklung & Inhalte", body: "Wir bauen die Webseite mit modernen Web-Technologien, integrieren Ihre Inhalte und richten das CMS ein. Auf Wunsch übernehmen wir auch das Texten." },
              { title: "Launch & SEO", body: "Wir gehen mit Ihnen live, richten Google Search Console und Google Business Profil ein – und Sie sind ab Tag 1 sichtbar." },
              { title: "Managed Webservice", body: "Nach dem Launch übernehmen wir auf Wunsch die laufende Betreuung: Updates, Sicherheits-Patches, Backups, Performance-Monitoring sowie kleine Inhalts- und Designanpassungen. So bleibt Ihre Webseite langfristig schnell, sicher und aktuell – Sie konzentrieren sich auf Ihr Geschäft, wir auf den Rest." },
            ].map((s, i) => (
              <li key={i} className="grid grid-cols-12 gap-6 border-b border-line py-7 items-baseline" data-testid={`webdesign-step-${i}`}>
                <span className="col-span-2 sm:col-span-1 font-display font-black text-3xl text-accent">{String(i + 1).padStart(2, "0")}</span>
                <div className="col-span-10 sm:col-span-11">
                  <h4 className="font-display font-bold text-xl text-ink">{s.title}</h4>
                  <p className="mt-2 text-muted text-sm leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading eyebrow="Pakete" title="Drei Wege zu Ihrer neuen Webseite." sub="Alle Pakete enthalten Hosting in Deutschland, SSL, mobile Optimierung, SEO-Setup und ein CMS zum Selbstpflegen." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                tier: "Starter",
                price: "ab 1.200 €",
                ideal: "Selbständige, Handwerker, kleine Praxen",
                features: ["Bis 5 Unterseiten", "Mobil-optimiertes Design", "Basis-SEO + Google Business", "Kontaktformular", "1 Schulung (60 min)", "Hosting & SSL inkl. (1 Jahr)"],
                accent: false,
              },
              {
                tier: "Business",
                price: "ab 3.500 €",
                ideal: "KMU, Kanzleien, Hotels, Beratung",
                features: ["Bis 15 Unterseiten + Blog", "Mehrsprachig (DE/EN)", "Erweitertes lokales SEO", "CMS mit Bild- & Text-Upload", "Performance-Optimierung", "Hosting, SSL, Backup, Wartung 1 Jahr"],
                accent: true,
              },
              {
                tier: "Custom",
                price: "individuell",
                ideal: "Online-Shops, Web-Anwendungen, Portale",
                features: ["Beliebig viele Seiten", "Anbindung an ERP / CRM", "Online-Shop / Buchungssystem", "Admin-Dashboard nach Maß", "API-Integrationen", "Full Managed Service"],
                accent: false,
              },
            ].map((p, i) => (
              <div
                key={p.tier}
                className={`border p-8 flex flex-col ${p.accent ? "bg-primary text-white border-primary shadow-glow" : "bg-white border-line lift"}`}
                data-testid={`webdesign-package-${p.tier.toLowerCase()}`}
              >
                <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${p.accent ? "text-accent" : "text-primary"}`}>{p.tier}</span>
                <div className={`mt-3 font-display font-black text-4xl ${p.accent ? "text-white" : "text-ink"}`}>{p.price}</div>
                <div className={`mt-2 text-sm ${p.accent ? "text-white/80" : "text-muted"}`}>{p.ideal}</div>
                <ul className="mt-6 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${p.accent ? "text-white" : "text-ink"}`}>
                      <CheckCircle2 size={15} className={p.accent ? "text-accent" : "text-primary"} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/kontakt"
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 font-medium transition-colors ${
                    p.accent ? "bg-accent text-ink hover:bg-accent/90" : "border border-primary text-primary hover:bg-primary hover:text-white"
                  }`}
                >
                  Anfragen <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink">FAQ.</h2>
          <div className="mt-12 space-y-4">
            {[
              { q: "Was kostet eine professionelle Webseite in Fulda?", a: "Eine Visitenkarten-Webseite startet bei rund 1.200 €. KMU-Webseiten mit CMS und Mehrsprachigkeit liegen zwischen 3.500 € und 8.000 €. Größere Web-Anwendungen werden individuell kalkuliert. Wir machen ein verbindliches Festpreis-Angebot nach einem 30-minütigen Erstgespräch." },
              { q: "Wie lange dauert die Erstellung?", a: "Eine kompakte Visitenkarten-Seite ist in 2–3 Wochen live. KMU-Webseiten mit CMS, Mehrsprachigkeit und SEO-Setup brauchen typischerweise 4–8 Wochen." },
              { q: "Übernehmen Sie auch das lokale SEO?", a: "Ja. Lokales SEO ist Standard in jedem unserer Projekte: Google Business Profil, lokale Keyword-Optimierung, Schema.org-Daten, Verzeichnis-Einträge und Reputation Management für Fulda, Bad Hersfeld, Hünfeld, Kassel, Erfurt und Würzburg." },
              { q: "Kann ich meine Webseite selbst bearbeiten?", a: "Ja. Wir liefern jede Webseite mit einem einfachen CMS, in dem Sie Texte, Bilder, Blog-Beiträge und neue Seiten selbst verwalten. Eine Schulung ist im Lieferumfang." },
              { q: "Wo wird meine Webseite gehostet?", a: "Ausschließlich in deutschen Rechenzentren. DSGVO-konform, SSL inklusive, tägliche Backups, 99,9 % Verfügbarkeit." },
              { q: "Was passiert nach dem Launch?", a: "Wir bieten Wartungspakete ab 49 €/Monat – inklusive Updates, Sicherheits-Patches, Backup-Monitoring und kleine Inhaltsänderungen. Sie können aber auch alles selbst übernehmen." },
            ].map((f, i) => (
              <details key={i} className="border border-line bg-white p-6 group" data-testid={`webdesign-faq-${i}`}>
                <summary className="font-display font-bold text-lg text-ink cursor-pointer flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-4 text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <Code2 size={40} className="text-accent mx-auto" />
          <h2 className="mt-6 font-display font-black text-3xl sm:text-5xl tracking-tightest leading-[1.05] max-w-3xl mx-auto">
            Bereit für eine Webseite, die gefunden wird?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">
            30 Minuten Erstgespräch · Festpreis-Angebot in 3 Werktagen · Webseiten, die in Fulda, Bad Hersfeld, Hünfeld, Kassel, Erfurt und Würzburg ranken.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="webdesign-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              Beratungsgespräch anfragen <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+4966299153890"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              +49 (0) 6629 9153890
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
